import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Box,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import { getWindForecast } from "../../../api/regatta";
import OpacityIcon from "@material-ui/icons/Opacity";

/** Estado del cielo → emoji. Sustituye al icono que servía OpenWeather. */
const SKY_EMOJI = {
  clear: "☀️",
  "mostly-clear": "🌤️",
  "partly-cloudy": "⛅",
  cloudy: "☁️",
  fog: "🌫️",
  drizzle: "🌦️",
  rain: "🌧️",
  showers: "🌦️",
  snow: "❄️",
  thunderstorm: "⛈️",
};

export default function WindDialog({ open, onClose, regattaId }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && regattaId) {
      setLoading(true);
      getWindForecast(regattaId)
        .then((res) => {
          console.log("Respuesta forecast:", res.data);
          setForecast(res.data);
        })
        .catch((err) => {
          console.error("Error loading wind forecast", err);
          setForecast(null);
        })
        .finally(() => setLoading(false));
    }
  }, [open, regattaId]);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString("es-ES");
  };

  const resumen = forecast?.hourly?.length
    ? (() => {
        const condiciones = forecast.hourly.map((h) => h.weather?.description).filter(Boolean);
        const unicas = [...new Set(condiciones)];
        return unicas.join(", ");
      })()
    : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Forecast {forecast ? formatDate(forecast?.regattaDate) : ""}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress />
        ) : forecast ? (
          forecast.hourly && forecast.hourly.length > 0 ? (
            <>
              <Box mb={2}>
                {resumen && (
                  <Typography variant="body2" color="textSecondary">
                    Expected conditions: {resumen}
                  </Typography>
                )}
              </Box>

              {/* TABLA */}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hour</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Temp. (°C)</TableCell>
                    <TableCell>Rainfall (%)</TableCell>
                    <TableCell>Wind Speed</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Gusts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forecast.hourly.slice(0, 12).map((h) => (
                    <TableRow key={h.timestamp}>
                      <TableCell>
                        {new Date(h.timestamp * 1000).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>

                      <TableCell>
                        {h.weather?.icon ? (
                          <Tooltip title={h.weather.description || "Sin descripción"}>
                            <span
                              role="img"
                              aria-label={h.weather.description || "tiempo"}
                              style={{ fontSize: "1.4em", verticalAlign: "middle" }}
                            >
                              {SKY_EMOJI[h.weather.icon] || "·"}
                            </span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </TableCell>

                      <TableCell>
                        {h.temp != null ? `${h.temp.toFixed(1)}°C` : "-"}
                      </TableCell>

                      <TableCell>
                        {h.pop != null ? `${Math.round(h.pop * 100)}%` : "-"}
                        {h.pop >= 0.5 && (
                          <Tooltip title="High probability of rain">
                            <OpacityIcon style={{ color: "#2196f3", marginLeft: 4 }} fontSize="small" />
                          </Tooltip>
                        )}
                      </TableCell>

                      <TableCell>
                        {h.wind_speed?.toFixed(1) ?? "-"}
                        <span style={{ fontSize: "0.75em", marginLeft: 4 }}>kn</span>
                      </TableCell>

                      <TableCell>
                        {h.wind_deg}°
                        <span
                          style={{
                            display: "inline-block",
                            marginLeft: 6,
                            transform: `rotate(${h.wind_deg}deg)`,
                          }}
                        >
                          ➤
                        </span>
                      </TableCell>

                      <TableCell>
                        {h.wind_gust?.toFixed(1) ?? "-"}
                        <span style={{ fontSize: "0.75em", marginLeft: 4 }}>kn</span>
                        {h.wind_gust > 20 ? (
                          <Tooltip title="Strong gusts">
                            <WarningIcon style={{ color: "#f57c00", marginLeft: 6 }} fontSize="small" />
                          </Tooltip>
                        ) : h.wind_gust > 16 ? (
                          <Tooltip title="Moderate gusts">
                            <WarningIcon style={{ color: "#ffc062ff", marginLeft: 6 }} fontSize="small" />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography>
              No data available yet for the day{" "}
              {forecast.regattaDate
                ? formatDate(forecast.regattaDate)
                : "seleccionado"}
              . The forecast only covers the next 5 days.
            </Typography>
          )
        ) : (
          <Typography>The forecast could not be obtained.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
